/* announcement.js — <sf-announcement-rotator> (sections/announcement-bar.liquid, display_mode "rotate").

   One message at a time. Every slide sits in the same grid cell (CSS), so the bar keeps the height of its longest
   message and nothing below it moves when the message changes; the visible one is .is-active, the others are
   aria-hidden + inert so links in hidden messages cannot be tabbed to.
   · Auto-advance every data-interval ms. A manual ‹ › change restarts the timer, so the chosen message gets a full
     interval before the next one replaces it.
   · Paused while the pointer is over the bar and while anything inside it has keyboard focus; never auto-advances
     under prefers-reduced-motion (the buttons still work, and the change is instant). Paused while the tab is hidden.
   · Manual changes are announced (aria-live polite for that change only); automatic ones are not, so a screen reader
     is not interrupted every few seconds.
   · ArrowLeft / ArrowRight on the buttons move as well; RTL swaps them so "next" is always in reading order.
   · Theme editor: selecting a message block shows it and stops rotation until it is deselected. */
'use strict';
(function () {
  if (customElements.get('sf-announcement-rotator')) return;
  const reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  customElements.define('sf-announcement-rotator', class extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-ann-slide]'));
      this.viewport = this.querySelector('[data-ann-viewport]');
      this.interval = Math.max(2000, parseInt(this.dataset.interval, 10) || 5000);
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this._ctrl = new AbortController();
      const signal = this._ctrl.signal;
      this._hover = false; this._focus = false; this._editor = false;
      if (this.slides.length < 2) return;
      const prev = this.querySelector('[data-ann-prev]');
      const next = this.querySelector('[data-ann-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1, true), { signal });
      if (next) next.addEventListener('click', () => this.go(this.index + 1, true), { signal });
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        if (!e.target.closest('[data-ann-prev], [data-ann-next]')) return;
        e.preventDefault();
        const rtl = getComputedStyle(this).direction === 'rtl';
        const fwd = (e.key === 'ArrowRight') !== rtl;
        this.go(this.index + (fwd ? 1 : -1), true);
      }, { signal });
      this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { this._hover = true; this.sync(); } }, { signal });
      this.addEventListener('pointerleave', () => { this._hover = false; this.sync(); }, { signal });
      /* keyboard focus only: a mouse click on ‹ › also focuses the button, and that must not freeze the rotation */
      this.addEventListener('focusin', (e) => { let kb = true; try { kb = e.target.matches(':focus-visible'); } catch (err) { /* old browser: treat as keyboard */ } this._focus = kb; this.sync(); }, { signal });
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) { this._focus = false; this.sync(); } }, { signal });
      document.addEventListener('visibilitychange', () => this.sync(), { signal });
      if (reduce.addEventListener) reduce.addEventListener('change', () => this.sync(), { signal });
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (i > -1) { this._editor = true; this.go(i, false); }
      }, { signal });
      document.addEventListener('shopify:block:deselect', () => { this._editor = false; this.sync(); }, { signal });
      this.sync();
    }
    disconnectedCallback() {
      if (this._ctrl) this._ctrl.abort();
      clearTimeout(this._timer);
    }
    paused() { return this._hover || this._focus || this._editor || document.hidden || reduce.matches; }
    /* (re)start the countdown for the message on show; nothing runs while paused */
    sync() {
      clearTimeout(this._timer);
      if (this.slides.length < 2 || this.paused()) return;
      this._timer = setTimeout(() => this.go(this.index + 1, false), this.interval);
    }
    go(i, manual) {
      const n = this.slides.length;
      const to = ((i % n) + n) % n;
      if (to !== this.index) {
        const from = this.slides[this.index];
        const show = this.slides[to];
        from.classList.remove('is-active'); from.setAttribute('aria-hidden', 'true'); from.inert = true;
        show.classList.add('is-active'); show.removeAttribute('aria-hidden'); show.inert = false;
        this.index = to;
        if (this.viewport) {
          this.viewport.setAttribute('aria-live', manual ? 'polite' : 'off');
          if (manual) setTimeout(() => this.viewport && this.viewport.setAttribute('aria-live', 'off'), 1200);
        }
      }
      this.sync();
    }
  });
})();
